// A number <input> that lets the user type freely (including transient invalid
// values like "" or numbers outside [min,max]) and commits a clamped integer
// only on blur / Enter. The naive "clamp inside onChange" pattern breaks any
// value whose intermediate digits violate the range — e.g. typing "12" when
// max=9 snaps the displayed value back to 9 after the "1" frame. While the
// field has focus the parent's `value` is ignored; once it loses focus we
// re-sync. Shared by StepProgram (stamps/milestones) + ExpirationFields.

import { useEffect, useRef, useState } from 'react'

export function ClampedNumberInput({
  value,
  min,
  max,
  disabled,
  onCommit,
  className,
  ariaLabel,
}: {
  value: number
  min: number
  max: number
  disabled?: boolean | undefined
  onCommit: (v: number) => void
  className?: string | undefined
  ariaLabel: string
}) {
  const [draft, setDraft] = useState(String(value))
  const focused = useRef(false)

  // Keep the visible text in sync with the prop when the input isn't being
  // edited (e.g. another control bumped the value).
  useEffect(() => {
    if (!focused.current) setDraft(String(value))
  }, [value])

  function commit() {
    const n = Number(draft)
    const clamped = Number.isFinite(n)
      ? Math.min(max, Math.max(min, Math.trunc(n)))
      : value
    setDraft(String(clamped))
    if (clamped !== value) onCommit(clamped)
  }

  return (
    <input
      type="number"
      className={className}
      min={min}
      max={max}
      value={draft}
      disabled={disabled}
      onFocus={() => {
        focused.current = true
      }}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        focused.current = false
        commit()
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          ;(e.currentTarget as HTMLInputElement).blur()
        }
      }}
      aria-label={ariaLabel}
    />
  )
}
